import React, { useContext, useMemo } from 'react';
import {
  AppMenuButton,
  AppMenuList,
  AppMenuListExpandableItem,
  AppMenuListItem,
  ButtonGroup,
  ElementObserver,
  HelpIcon,
  LanguageIcon,
  LogoutIcon,
  MoreVerticalIcon,
  SupportTicketSignalOutline,
  TeamIcon,
  ZeroStateAddIcon
} from '@m/alchemy-ui';
import { FormattedMessage, useIntl } from 'react-intl';
import { Link } from 'react-location';
import {
  useInvitesDialog,
  useIsMobile,
  useIsNarrowMobile,
  useLanguageOverride,
  useLanguageSwitcher,
  usePostIdeaDialog,
  useRegisterDialog,
  useSignInDialog,
  useUserCreatedChallengeForm
} from '../../hooks';
import { CommunityContext, useRuntimeConfig, useRuntimeUser } from '../../context';
import { defaultRuntimeUser, RouteLink } from '../../common/interfaces';
import { CommunityLogo, MobileMenu, OverflowMenu, SearchButton } from './components';
import * as Styled from './Navigation.styled';
import { SocialMediaMenu } from './components/SocialMediaMenu';

export const languageIsoMatch = (l1: string, l2: string) =>
  l1.toLowerCase().replace(/[^a-zA-Z]/g, '') === l2.toLowerCase().replace(/[^a-zA-Z]/g, '');

export const Navigation = React.forwardRef(function Navigation(
  {
    links,
    isSticky = true
  }: {
    links?: RouteLink[];
    isSticky?: boolean | undefined;
  },
  ref: React.Ref<HTMLElement>
) {
  const intl = useIntl();
  const isMobile = useIsMobile();
  const isNarrowMobile = useIsNarrowMobile();
  const community = useContext(CommunityContext);
  const languageOverride = useLanguageOverride();
  const switchLanguage = useLanguageSwitcher();
  const { idp } = useRuntimeConfig();

  const { id, canInvite, permissions } = useRuntimeUser();
  const isLoggedIn = id !== defaultRuntimeUser.id;
  const { loadDialog: loadSignInDialog } = useSignInDialog();
  const { loadDialog: loadRegisterDialog } = useRegisterDialog();
  const { loadDialog: loadPostIdeaDialog } = usePostIdeaDialog();
  const { loadDialog: loadUserCreatedChallengeForm } = useUserCreatedChallengeForm();
  const { loadDialog: loadInvitesDialog } = useInvitesDialog();
  const canModerateIdeas = permissions?.isApprovalModerator || permissions?.isModerator;
  const canCreateChallenge = permissions?.isChallengeCreator;

  const mobileMenuButtonActions = useMemo((): { label: string; onClick: () => void }[] => {
    const items = [];

    if (canModerateIdeas) {
      items.push({
        label: intl.formatMessage({ id: 'moderateIdeas', defaultMessage: 'Moderate Ideas' }),
        onClick() {
          window.location.href = '/dashboard';
        }
      });
    }

    if (canCreateChallenge) {
      items.push({
        label: intl.formatMessage({ id: 'createAChallenge', defaultMessage: 'Create a Challenge' }),
        onClick: loadUserCreatedChallengeForm
      });
    }

    return items;
  }, [canModerateIdeas, canCreateChallenge, loadUserCreatedChallengeForm]); // eslint-disable-line

  return (
    <Styled.AppBarWrapper ref={ref} isSticky={isSticky}>
      <Styled.AppBar priority="adminPrimary" colorScheme="alchemyLight">
        <Styled.AppPrimaryBar>
          {!isMobile && (
            <Link to="/">
              <CommunityLogo />
            </Link>
          )}
          <Styled.AppNav>
            {isMobile ? (
              <MobileMenu isLoggedIn={isLoggedIn} links={links} />
            ) : (
              <OverflowMenu isLoggedIn={isLoggedIn} links={links} />
            )}
          </Styled.AppNav>
        </Styled.AppPrimaryBar>
        <Styled.AppSecondaryBar>
          <Styled.SearchButtonWrapper>
            <SearchButton />
          </Styled.SearchButtonWrapper>
          <SocialMediaMenu />
          {isMobile ? (
            isLoggedIn ? (
              <>
                <ElementObserver>
                  {({ observedElementWidth, observeTarget }) => {
                    const hideTextButton =
                      isNarrowMobile && ((observedElementWidth || 0) > 140 || observedElementWidth === 0);
                    return (
                      <>
                        <Styled.AppNavButton onClick={loadPostIdeaDialog} ref={observeTarget} hide={hideTextButton}>
                          <ZeroStateAddIcon size={12} aria-hidden="true" />
                          {languageOverride(
                            'postAnIdea',
                            intl.formatMessage({ id: 'postAnIdea', defaultMessage: 'Post an Idea' })
                          )}
                        </Styled.AppNavButton>
                        <Styled.IconButton
                          icon={<ZeroStateAddIcon size={22} aria-hidden="true" />}
                          tooltipContents={languageOverride(
                            'postAnIdea',
                            intl.formatMessage({ id: 'postAnIdea', defaultMessage: 'Post an Idea' })
                          )}
                          onClick={loadPostIdeaDialog}
                          hide={!hideTextButton}
                        />
                      </>
                    );
                  }}
                </ElementObserver>
                {mobileMenuButtonActions.length > 0 && (
                  <Styled.MenuButton data-testid="MoreButton" priority="tertiary" actions={mobileMenuButtonActions}>
                    {intl.formatMessage({ id: 'more', defaultMessage: 'More' })}
                  </Styled.MenuButton>
                )}
              </>
            ) : (
              <>
                <Styled.AppNavButton priority="secondary" onClick={loadRegisterDialog}>
                  <FormattedMessage id="register" defaultMessage="Register" />
                </Styled.AppNavButton>
                <Styled.AppNavButton onClick={loadSignInDialog} alignLeft>
                  <FormattedMessage id="signIn" defaultMessage="Sign In" />
                </Styled.AppNavButton>
              </>
            )
          ) : (
            <ButtonGroup>
              {isLoggedIn ? (
                <>
                  <Styled.AppNavButton onClick={loadPostIdeaDialog}>
                    <ZeroStateAddIcon size={16} aria-hidden="true" />
                    {languageOverride(
                      'postAnIdea',
                      intl.formatMessage({ id: 'postAnIdea', defaultMessage: 'Post an Idea' })
                    )}
                  </Styled.AppNavButton>
                  <ButtonGroup isJoined>
                    {canModerateIdeas && (
                      <Styled.AppNavButton priority="secondary" href="/dashboard">
                        {intl.formatMessage({ id: 'moderateIdeas', defaultMessage: 'Moderate Ideas' })}
                      </Styled.AppNavButton>
                    )}
                    {canCreateChallenge && (
                      <Styled.AppNavButton priority="secondary" onClick={loadUserCreatedChallengeForm}>
                        {intl.formatMessage({ id: 'createAChallenge', defaultMessage: 'Create a Challenge' })}
                      </Styled.AppNavButton>
                    )}
                  </ButtonGroup>
                </>
              ) : (
                <>
                  <Styled.AppNavButton priority="secondary" onClick={loadRegisterDialog}>
                    <FormattedMessage id="register" defaultMessage="Register" />
                  </Styled.AppNavButton>
                  <Styled.AppNavButton onClick={loadSignInDialog}>
                    <FormattedMessage id="signIn" defaultMessage="Sign In" />
                  </Styled.AppNavButton>
                </>
              )}
            </ButtonGroup>
          )}
          <Styled.ButtonWrapper>
            <AppMenuButton
              label={intl.formatMessage({ id: 'more', defaultMessage: 'More' })}
              shouldHideLabel
              mobileMenuMaxHeight="50vh"
              icon={<MoreVerticalIcon size={24} aria-hidden="true" />}
              overlay={() => (
                <AppMenuList>
                  <AppMenuListItem icon={<HelpIcon size={16} aria-hidden="true" />}>
                    <a href="https://help.crowdicity.com">
                      <FormattedMessage id="help" defaultMessage="Help" />
                    </a>
                  </AppMenuListItem>
                  {isLoggedIn && canInvite && (
                    <AppMenuListItem hasDivider icon={<TeamIcon size={16} aria-hidden="true" />}>
                      <a
                        href="/"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          loadInvitesDialog();
                        }}
                      >
                        <FormattedMessage id="Invite Users" defaultMessage="Invite Users" />
                      </a>
                    </AppMenuListItem>
                  )}
                  {community?.options?.allowLanguageChoice && (
                    <AppMenuListExpandableItem
                      label={intl.formatMessage({ id: 'languages', defaultMessage: 'Languages' })}
                      icon={<LanguageIcon size={16} aria-hidden="true" />}
                    >
                      <AppMenuList>
                        {(community?.options?.allowedLanguages || []).map(({ name, iso }) => (
                          <AppMenuListItem key={iso} isActive={languageIsoMatch(intl.locale, iso)}>
                            <a href="/" onClick={(e) => switchLanguage(iso, e)}>
                              {name}
                            </a>
                          </AppMenuListItem>
                        ))}
                      </AppMenuList>
                    </AppMenuListExpandableItem>
                  )}
                  {community?.options?.crowdicityAsIDPForSalesforce &&
                    idp?.region_idp_url &&
                    idp?.sp?.salesforce?.entityid && (
                      <AppMenuListItem hasDivider icon={<SupportTicketSignalOutline size={16} aria-hidden="true" />}>
                        <a
                          target="_blank"
                          href={`${idp.region_idp_url}/saml2/idp/SSOService.php?spentityid=${idp.sp.salesforce.entityid}&hid=${community.id}`}
                        >
                          <FormattedMessage id="submit" defaultMessage="Submit a ticket" />
                        </a>
                      </AppMenuListItem>
                    )}
                  {isLoggedIn && (
                    <AppMenuListItem hasDivider icon={<LogoutIcon size={16} aria-hidden="true" />}>
                      <a href="/user/logout">
                        <FormattedMessage id="logOut" defaultMessage="Log Out" />
                      </a>
                    </AppMenuListItem>
                  )}
                </AppMenuList>
              )}
            />
          </Styled.ButtonWrapper>
        </Styled.AppSecondaryBar>
      </Styled.AppBar>
    </Styled.AppBarWrapper>
  );
});
